import type { IndustryKey } from "@sagot/constants";
import type { ReactNode } from "react";

interface IconProps {
  className?: string;
}

/** Glyphs of the register wizard, copied from the mockup's sprite (`#i-check`, `#i-left`, `#i-x`, `#i-globe`, the industry set). Decorative. */
function Glyph({ viewBox, className, children }: IconProps & { viewBox: string; children: ReactNode }) {
  return (
    <svg viewBox={viewBox} aria-hidden="true" focusable="false" className={className}>
      {children}
    </svg>
  );
}

export function CheckGlyph({ className }: IconProps) {
  return (
    <Glyph viewBox="0 0 16 16" className={className}>
      <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Glyph>
  );
}

export function BackGlyph({ className }: IconProps) {
  return (
    <Glyph viewBox="0 0 16 16" className={className}>
      <path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Glyph>
  );
}

export function CloseGlyph({ className }: IconProps) {
  return (
    <Glyph viewBox="0 0 16 16" className={className}>
      <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Glyph>
  );
}

export function GlobeGlyph({ className }: IconProps) {
  return (
    <Glyph viewBox="0 0 20 20" className={className}>
      <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3 10h14M10 3c2 2 3 4.3 3 7s-1 5-3 7c-2-2-3-4.3-3-7s1-5 3-7Z" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </Glyph>
  );
}

/** Success tick of the ready pane: a stroked path, drawn by CSS. */
export function DoneTick({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={className}>
      <path d="M5 12.5l4.5 4.5L19 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const INDUSTRY_GLYPHS: Readonly<Record<Exclude<IndustryKey, "other">, ReactNode>> = {
  education: (
    <path
      d="M10 3L2 7l8 4 8-4-8-4ZM5 8.8V13c1.5 1.5 3.2 2 5 2s3.5-.5 5-2V8.8M18 7v5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  ),
  healthcare: (
    <path
      d="M10 5c-1.5-1.5-5-2-6 .5-.8 2 .5 4 1 6s.5 5 2 5.5 1.5-3.5 3-3.5 1.5 4 3 3.5 1.5-3.5 2-5.5 1.8-4 1-6C15 3 11.5 3.5 10 5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  ),
  real_estate: (
    <>
      <path d="M3 9l7-5.5L17 9v8H3V9Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 17v-5h4v5" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </>
  ),
  hospitality: (
    <path
      d="M10 9c-1 3-1 6 0 8.5M10 9C8 6 4.5 5.5 3 7c2.5 0 4.5.5 7 2Zm0 0c2-3 5.5-3.5 7-2-2.5 0-4.5.5-7 2Zm0 0C9.5 6 7.5 3.5 5.5 3.5 7.5 5 9 6.5 10 9Zm0 0c.5-3 2.5-5.5 4.5-5.5C12.5 5 11 6.5 10 9ZM5 17.5h10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  ),
  human_resources: (
    <>
      <rect x="3" y="4" width="14" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="10" cy="9.5" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6.5 14.5c.6-1.4 2-2.2 3.5-2.2s2.9.8 3.5 2.2M8 2.5h4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  government: (
    <path
      d="M2.5 7.5L10 3l7.5 4.5M4 8v7M8 8v7M12 8v7M16 8v7M2.5 17h15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  ecommerce: (
    <>
      <path d="M4 7h12l-1 10H5L4 7Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M7.5 7V5.5a2.5 2.5 0 0 1 5 0V7" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </>
  ),
  business: (
    <>
      <rect x="4" y="2.5" width="12" height="15" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 7h6M7 10h6M7 13h3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
};

/** The industry's glyph from the mockup; "Other" has none (the mockup's chip is text only). */
export function IndustryGlyph({ industry, className }: IconProps & { industry: IndustryKey }) {
  if (industry === "other") return null;
  return (
    <Glyph viewBox="0 0 20 20" className={className}>
      {INDUSTRY_GLYPHS[industry]}
    </Glyph>
  );
}
